import { useEffect, useState } from 'react';

import { useTypedTranslation } from '~/shared/hooks';
import type { Locale } from '~/universal/constants';

import type { BlogMenuData, PopularArticleMenuItem, TopRatedCategoryMenuItem } from './helpers/blog-menu-cache';
import {
  fetchBlogMenu,
  getCachedBlogMenu,
  LIMIT_OF_POSTS,
  LIMIT_OF_TOP_RATED_CATEGORIES,
} from './helpers/blog-menu-cache';

type UseBlogMenuProps = Readonly<{
  lng: Locale;
}>;

type PopularArticleListItem =
  | PopularArticleMenuItem
  | Readonly<{
      _id: string;
      item: null;
    }>;

type TopRatedCategoryListItem =
  | TopRatedCategoryMenuItem
  | Readonly<{
      _id: string;
      item: null;
    }>;

type BlogMenuState = Readonly<{
  popularArticles: PopularArticleListItem[];
  topRatedCategories: TopRatedCategoryListItem[];
  loading: boolean;
  error: boolean;
}>;

const createPopularArticleSkeletons = (): PopularArticleListItem[] => {
  return Array.from({ length: LIMIT_OF_POSTS }, () => ({
    _id: crypto.randomUUID(),
    item: null,
  }));
};

const createTopRatedCategorySkeletons = (): TopRatedCategoryListItem[] => {
  return Array.from({ length: LIMIT_OF_TOP_RATED_CATEGORIES }, () => ({
    _id: crypto.randomUUID(),
    item: null,
  }));
};

const createInitialState = (lng: Locale): BlogMenuState => {
  const cachedData = getCachedBlogMenu(lng);

  if (cachedData) {
    return {
      popularArticles: cachedData.popularArticles,
      topRatedCategories: cachedData.topRatedCategories,
      loading: false,
      error: false,
    };
  }

  return {
    popularArticles: createPopularArticleSkeletons(),
    topRatedCategories: createTopRatedCategorySkeletons(),
    loading: true,
    error: false,
  };
};

const useBlogMenu = ({ lng }: UseBlogMenuProps) => {
  const t = useTypedTranslation(['text', 'page', 'link']);
  const [state, setState] = useState<BlogMenuState>(() => createInitialState(lng));

  useEffect(() => {
    const cachedData = getCachedBlogMenu(lng);

    if (cachedData) {
      setState({
        popularArticles: cachedData.popularArticles,
        topRatedCategories: cachedData.topRatedCategories,
        loading: false,
        error: false,
      });

      return;
    }

    let cancelled = false;

    const syncBlogMenu = async () => {
      try {
        const data: BlogMenuData = await fetchBlogMenu(lng);

        if (cancelled) {
          return;
        }

        setState({
          popularArticles: data.popularArticles,
          topRatedCategories: data.topRatedCategories,
          loading: false,
          error: false,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Failed to fetch blog menu:', error);

        setState({
          popularArticles: [],
          topRatedCategories: [],
          loading: false,
          error: true,
        });
      }
    };

    void syncBlogMenu();

    return () => {
      cancelled = true;
    };
  }, [lng]);

  return {
    t,
    loading: state.loading,
    error: state.error,
    popularArticles: state.popularArticles,
    topRatedCategories: state.topRatedCategories,
  };
};

export default useBlogMenu;
